'use client';

import { useSuspenseQuery } from '@tanstack/react-query';

import { useSearchParamsQuery } from './useSearchParamsQuery';
import useSearchParamPagination from './useSearchParamPagination';
import { sanitizeSearchParams } from '../table-filters/typed-filter-config';

import { buildSanitizedKey } from './searchParamsUtils';
import type { Pagination, TablePagination } from '../data-table/types';
import type {
  FilterConfigMap,
  InferSearchParams,
  PaginationSearchParams,
} from '../table-filters/typed-filter-config';

/**
 * Configuration for useSuspenseList hook.
 *
 * @typeParam TData - The raw API response type
 * @typeParam TRow - The row type extracted from the response
 * @typeParam TParams - The query parameters type sent to the API
 * @typeParam TFilterConfig - The typed filter config (from `as const satisfies FilterConfigMap`)
 */
export interface UseSuspenseListConfig<
  TData,
  TRow,
  TParams extends Record<string, unknown> = Record<string, unknown>,
  TFilterConfig extends FilterConfigMap = FilterConfigMap,
> {
  /** Cache tag(s) for React Query */
  cacheTag: string | string[];

  /** Fetcher function that accepts query params and returns data */
  fetcher: (params: TParams | undefined) => Promise<TData>;

  /** Extract the data array from the API response */
  getDataFromResponse: (response: TData) => TRow[];

  /** Extract pagination info from the API response */
  getPaginationFromResponse: (response: TData) => Pagination;

  /** Typed filter config (from `as const satisfies FilterConfigMap`) */
  filterConfig: TFilterConfig;

  /**
   * Build query parameters from sanitized filter params and pagination params.
   * Filter params are typed and runtime-validated against filterConfig.
   * Pagination params are the well-known cursor params (nextCursor, prevCursor).
   */
  buildQueryParams: (
    filterParams: InferSearchParams<TFilterConfig>,
    paginationParams: PaginationSearchParams,
  ) => TParams;

  /** Build custom query key */
  buildQueryKey?: (cacheTag: string | string[], searchKey: string) => unknown[];

  /** Options for useSearchParamsQuery hook */
  searchParamsOptions?: { sortEntries?: boolean };
}

interface UseSuspenseListReturn<TRow> {
  /** The fetched data array */
  data: TRow[];

  /** Pagination state (pass to PaginationFooter) */
  pagination: TablePagination;
}

/**
 * Hook for paginated list views with Suspense.
 * Bundles search params parsing, data fetching via useSuspenseQuery,
 * and cursor-based pagination into a single hook.
 *
 * Must be used inside a <Suspense> boundary.
 *
 * @example
 * ```tsx
 * const filterConfig = {
 *   status: {
 *     variant: 'select',
 *     filterOptions: [
 *       { value: 'active', label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' },
 *     ],
 *   },
 * } as const satisfies FilterConfigMap;
 *
 * const { data, pagination } = useSuspenseList<
 *   ResponseType, RowType, QueryParams, typeof filterConfig
 * >({
 *   cacheTag: 'items',
 *   filterConfig,
 *   fetcher: (params) => callServerAction('items.list', [params]),
 *   getDataFromResponse: (res) => res.data,
 *   getPaginationFromResponse: (res) => ({
 *     cursors: { nextCursor: res.pagination_token ?? null },
 *     totalCount: res.data.length,
 *   }),
 *   buildQueryParams: (filterParams, paginationParams) => ({
 *     status: filterParams.status,
 *     pagination_token: paginationParams.nextCursor,
 *   }),
 * });
 *
 * return (
 *   <>
 *     {data.map(item => <Card key={item.id} item={item} />)}
 *     <PaginationFooter {...pagination} totalCountLabel="items" />
 *   </>
 * );
 * ```
 */
export function useSuspenseList<
  TData,
  TRow,
  TParams extends Record<string, unknown> = Record<string, unknown>,
  TFilterConfig extends FilterConfigMap = FilterConfigMap,
>(
  config: UseSuspenseListConfig<TData, TRow, TParams, TFilterConfig>,
): UseSuspenseListReturn<TRow> {
  const {
    cacheTag,
    fetcher,
    getDataFromResponse,
    getPaginationFromResponse,
    filterConfig,
    buildQueryParams,
    buildQueryKey,
    searchParamsOptions,
  } = config;

  const { searchParamsObject: rawSearchParams, searchKey } = useSearchParamsQuery(
    searchParamsOptions ?? { sortEntries: true },
  );
  const searchParamsObject = rawSearchParams as Record<string, unknown> | undefined;

  const { filterParams, paginationParams } = sanitizeSearchParams(filterConfig, searchParamsObject);
  const queryParams = buildQueryParams(filterParams, paginationParams);

  // Default keys off sanitized params so UI-state params (e.g. ConfigurableTabs
  // deepLink) don't cause list refetches. Custom buildQueryKey receives the raw
  // searchKey for backwards compat.
  const sanitizedKey = buildSanitizedKey(filterParams, paginationParams);
  const queryKey = buildQueryKey ? buildQueryKey(cacheTag, searchKey) : [cacheTag, sanitizedKey];

  const { data: response } = useSuspenseQuery({
    queryKey,
    queryFn: () => fetcher(queryParams),
  });

  const data = getDataFromResponse(response);
  const pagination = useSearchParamPagination(getPaginationFromResponse(response));

  return { data, pagination };
}
